/**
 * API Service Layer
 *
 * Single axios instance with:
 *   - baseURL from env (REACT_APP_BACKEND_URL) or window.location.origin
 *   - Request interceptor: attach JWT from cookies
 *   - Response interceptor: handle 401 -> redirect to login
 *   - Helper functions: get, post, patch, del
 */

import axios from "axios";
import Cookies from "js-cookie";

/* ── Base URL ──────────────────────────────────────────────────────── */
const baseURL = (() => {
  const raw = process.env.REACT_APP_BACKEND_URL;
  if (raw && raw.trim() !== "") {
    return raw.replace(/\/$/, "");
  }
  return window.location.origin;
})();

/* ── Create axios instance ─────────────────────────────────────────── */
const api = axios.create({
  baseURL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 30000, // 30s timeout
});

/* ── Request interceptor: attach JWT from cookies ──────────────────── */
api.interceptors.request.use(
  (config) => {
    const tokenRaw = Cookies.get("token");
    if (tokenRaw) {
      try {
        // Token may be stored as JSON string
        const token = JSON.parse(tokenRaw);
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch {
        // If not JSON, use raw value
        if (tokenRaw) {
          config.headers.Authorization = `Bearer ${tokenRaw}`;
        }
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/* ── Response interceptor: handle 401 ──────────────────────────────── */
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      // Clear auth cookies
      Cookies.remove("token");
      Cookies.remove("user");

      // Redirect to login (avoid redirect loops)
      const currentPath = window.location.pathname;
      if (currentPath !== "/login" && currentPath !== "/") {
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

/* ── Helper functions ──────────────────────────────────────────────── */

/**
 * GET request.
 * @param {string} url     - Endpoint path (e.g. "/api/v1/users/me")
 * @param {object} [config] - Additional axios config
 * @returns {Promise<any>}  - Response data
 */
export async function get(url, config = {}) {
  const response = await api.get(url, config);
  return response.data;
}

/**
 * POST request.
 * @param {string} url      - Endpoint path
 * @param {object} [data]   - Request body
 * @param {object} [config] - Additional axios config
 * @returns {Promise<any>}  - Response data
 */
export async function post(url, data = {}, config = {}) {
  const response = await api.post(url, data, config);
  return response.data;
}

/**
 * PATCH request.
 * @param {string} url      - Endpoint path
 * @param {object} [data]   - Request body
 * @param {object} [config] - Additional axios config
 * @returns {Promise<any>}  - Response data
 */
export async function patch(url, data = {}, config = {}) {
  const response = await api.patch(url, data, config);
  return response.data;
}

/**
 * DELETE request.
 * @param {string} url      - Endpoint path
 * @param {object} [config] - Additional axios config
 * @returns {Promise<any>}  - Response data
 */
export async function del(url, config = {}) {
  const response = await api.delete(url, config);
  return response.data;
}

/**
 * Upload file via POST with FormData.
 * @param {string}   url       - Endpoint path
 * @param {FormData} formData  - FormData object
 * @param {function} [onProgress] - Upload progress callback (percentage 0-100)
 * @param {object}   [config]  - Additional axios config
 * @returns {Promise<any>}     - Response data
 */
export async function upload(url, formData, onProgress, config = {}) {
  const response = await api.post(url, formData, {
    ...config,
    headers: {
      ...config.headers,
      "Content-Type": "multipart/form-data",
    },
    onUploadProgress: onProgress
      ? (progressEvent) => {
          const pct = Math.round(
            (progressEvent.loaded * 100) / (progressEvent.total || 1)
          );
          onProgress(pct);
        }
      : undefined,
  });
  return response.data;
}

/* ── Export the raw instance for advanced use cases ────────────────── */
export default api;
